import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ProviderCardData } from "@/components/services/ProviderCard";
import type { FractionalExecutive } from "@/content/fractionals";
import { createProfileEngine } from "@/lib/providerProfileEngine";

const engineSingleton = createProfileEngine();

type ProfileEnrichment = {
  linkedin_url: string | null;
  photo_path: string | null;
  country: string | null;
  location: string | null;
  published_snapshot: {
    profile?: {
      intro_video_url?: string;
      location?: string;
      country?: string;
      hourly_rate_eur?: number;
    };
  } | null;
};

type CoachRow = {
  id: string;
  slug: string;
  user_id: string;
  display_name: string;
  headline: string | null;
  bio: string | null;
  expertise: string[];
  languages: string[];
  country: string | null;
  photo_path: string | null;
  hourly_rate_eur_cents: number | null;
  featured?: boolean | null;
  featured_rank?: number | null;
  featured_until?: string | null;
  pp_engine_profiles?: ProfileEnrichment | null;
};

type FractionalRow = {
  id: string;
  slug: string;
  user_id: string;
  display_name: string;
  role_type: string | null;
  headline: string | null;
  bio: string | null;
  expertise: string[];
  industries: string[];
  languages: string[];
  country: string | null;
  day_rate_eur_cents: number | null;
  featured?: boolean | null;
  featured_rank?: number | null;
  featured_until?: string | null;
  pp_engine_profiles?: ProfileEnrichment | null;
};

const COLORS = ["purple", "amber", "blue", "green", "rose"] as const;
function pickColor(seed: string): (typeof COLORS)[number] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return COLORS[h % COLORS.length];
}
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

export function useEngineCoaches() {
  return useQuery({
    queryKey: ["engine", "coach_listings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coach_listings")
        .select("id, slug, user_id, display_name, headline, bio, expertise, languages, country, photo_path, hourly_rate_eur_cents, featured, featured_rank, featured_until, pp_engine_profiles(linkedin_url, photo_path, country, location, published_snapshot)")
        .eq("published", true)
        .order("featured", { ascending: false })
        .order("featured_rank", { ascending: true, nullsFirst: false })
        .order("updated_at", { ascending: false });
      if (error) throw error;
      const now = Date.now();
      const rows = ((data ?? []) as CoachRow[]).map((r) => ({
        ...r,
        featured:
          r.featured === true &&
          (!r.featured_until || new Date(r.featured_until).getTime() > now),
      }));
      return rows;
    },
    staleTime: 60_000,
  });
}

export function coachRowToCard(row: CoachRow): ProviderCardData {
  const [first, ...rest] = (row.display_name || "").split(" ");
  const enr = row.pp_engine_profiles ?? null;
  const snapProfile = enr?.published_snapshot?.profile ?? null;
  const hourlyEur =
    row.hourly_rate_eur_cents != null
      ? Math.round(row.hourly_rate_eur_cents / 100)
      : typeof snapProfile?.hourly_rate_eur === "number"
        ? Math.round(snapProfile.hourly_rate_eur)
        : null;
  const photoPath = row.photo_path ?? enr?.photo_path ?? null;
  const photoUrl = engineSingleton.photoUrl(photoPath ?? undefined);
  const introVideoUrl = snapProfile?.intro_video_url ?? null;
  const city = snapProfile?.location || enr?.location || row.country || enr?.country || null;
  return {
    user_id: row.slug,
    first_name: first ?? null,
    last_name: rest.join(" ") || null,
    avatar_url: photoUrl,
    photo_url: photoUrl,
    intro_video_url: introVideoUrl,
    linkedin_url: enr?.linkedin_url ?? null,
    headline: row.headline,
    expertise_areas: row.expertise ?? [],
    languages: row.languages ?? [],
    rating_avg: null,
    rating_count: null,
    hourly_eur: hourlyEur,
    hourly_xof: hourlyEur ? Math.round(hourlyEur * 655.957) : null,
    demo: false,
    city,
    is_new: !row.featured,
    featured: row.featured === true,
    featured_until: row.featured_until ?? null,
    variant: "coach",
  };
}

export function useEngineFractionals() {
  return useQuery({
    queryKey: ["engine", "fractional_listings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fractional_listings")
        .select("id, slug, user_id, display_name, role_type, headline, bio, expertise, industries, languages, country, day_rate_eur_cents, featured, featured_rank, featured_until, pp_engine_profiles(linkedin_url, photo_path, country, location, published_snapshot)")
        .eq("published", true)
        .order("featured", { ascending: false })
        .order("featured_rank", { ascending: true, nullsFirst: false })
        .order("updated_at", { ascending: false });
      if (error) throw error;
      const now = Date.now();
      const rows = ((data ?? []) as FractionalRow[]).map((r) => ({
        ...r,
        featured:
          r.featured === true &&
          (!r.featured_until || new Date(r.featured_until).getTime() > now),
      }));
      return rows;
    },
    staleTime: 60_000,
  });
}

type ProviderProfileCoachRow = {
  user_id: string;
  headline_en: string | null;
  headline_fr: string | null;
  expertise_areas: string[] | null;
  languages: string[] | null;
  rating_avg: number | null;
  rating_count: number | null;
};

/**
 * Reads new-flow approved coaches (provider_applications → provider_profiles).
 * Returns rows in the same ProviderCardData shape as engine coaches so they can
 * be merged into the public /services/coaching grid.
 */
export function useProviderProfileCoaches(type: "coach" | "fractional" = "coach") {
  return useQuery({
    queryKey: ["provider_profiles", "directory", type],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("provider_profiles")
        .select("user_id, headline_en, headline_fr, expertise_areas, languages, rating_avg, rating_count")
        .eq("provider_type", type)
        .eq("published", true)
        .eq("lifecycle_status", "active");
      if (error) throw error;
      const rows = (data ?? []) as ProviderProfileCoachRow[];
      const ids = rows.map((r) => r.user_id);
      const byId = new Map<string, { first_name: string | null; last_name: string | null; avatar_url: string | null }>();
      if (ids.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id, first_name, last_name, avatar_url")
          .in("id", ids);
        for (const p of profs ?? []) {
          byId.set(p.id as string, {
            first_name: (p as { first_name: string | null }).first_name,
            last_name: (p as { last_name: string | null }).last_name,
            avatar_url: (p as { avatar_url: string | null }).avatar_url,
          });
        }
      }
      return rows.map((r) => ({ row: r, profile: byId.get(r.user_id) ?? null }));
    },
    staleTime: 60_000,
  });
}

export function providerProfileRowToCard(
  entry: { row: ProviderProfileCoachRow; profile: { first_name: string | null; last_name: string | null; avatar_url: string | null } | null },
  lang: "en" | "fr" = "en",
): ProviderCardData {
  const { row, profile } = entry;
  const headline = (lang === "fr" ? row.headline_fr : row.headline_en) ?? row.headline_en ?? row.headline_fr ?? null;
  return {
    user_id: row.user_id,
    first_name: profile?.first_name ?? null,
    last_name: profile?.last_name ?? null,
    avatar_url: profile?.avatar_url ?? null,
    photo_url: profile?.avatar_url ?? null,
    intro_video_url: null,
    linkedin_url: null,
    headline,
    expertise_areas: row.expertise_areas ?? [],
    languages: row.languages ?? [],
    rating_avg: row.rating_avg != null ? Number(row.rating_avg) : null,
    rating_count: row.rating_count != null ? Number(row.rating_count) : null,
    hourly_eur: null,
    hourly_xof: null,
    demo: false,
    city: null,
    is_new: true,
    featured: false,
    featured_until: null,
    variant: "coach",
  };
}

const ALLOWED_ROLES = new Set(["CFO", "COO", "CTO", "CMO", "CHRO", "CPO"]);
function normalizeRole(role: string | null | undefined): FractionalExecutive["role_type"] {
  const r = (role ?? "").toUpperCase().replace(/^ROLE\.FRACTIONAL_?/, "").trim();
  return (ALLOWED_ROLES.has(r) ? r : "COO") as FractionalExecutive["role_type"];
}

export function fractionalRowToExec(row: FractionalRow): FractionalExecutive {
  const dayEur = row.day_rate_eur_cents != null ? Math.round(row.day_rate_eur_cents / 100) : 600;
  const monthlyEurMid = dayEur * 10; // ~10 days/month
  const eurMin = Math.round(monthlyEurMid * 0.8);
  const eurMax = Math.round(monthlyEurMid * 1.4);
  const xofMin = Math.round(eurMin * 655.957);
  const xofMax = Math.round(eurMax * 655.957);
  return {
    id: row.id,
    slug: row.slug,
    demo: false,
    name: row.display_name,
    role_type: normalizeRole(row.role_type),
    headline: { en: row.headline ?? "", fr: row.headline ?? "" },
    monthly_rate_xof_min: xofMin,
    monthly_rate_xof_max: xofMax,
    monthly_rate_eur_min: eurMin,
    monthly_rate_eur_max: eurMax,
    hours_per_month_typical: "15-25",
    city: row.country ?? "",
    languages: row.languages ?? [],
    expertise_tags: row.expertise ?? [],
    bio: { en: row.bio ?? "", fr: row.bio ?? "" },
    engagements_completed: 0,
    avatar_initials: initialsOf(row.display_name),
    avatar_color: pickColor(row.id),
    engagement_scope: { en: "", fr: "" },
    deliverables_month_1: { en: [], fr: [] },
    typical_engagement_length_months: "3-12",
    ramp_model: { en: "", fr: "" },
    payout_rail: "stripe_connect",
  };
}

export function fractionalRowToCard(row: FractionalRow): ProviderCardData {
  const [first, ...rest] = (row.display_name || "").split(" ");
  const enr = row.pp_engine_profiles ?? null;
  const snapProfile = enr?.published_snapshot?.profile ?? null;
  const dayEur = row.day_rate_eur_cents != null ? Math.round(row.day_rate_eur_cents / 100) : null;
  const monthlyMid = dayEur != null ? dayEur * 10 : null;
  const monthlyMin = monthlyMid != null ? Math.round(monthlyMid * 0.8) : null;
  const monthlyMax = monthlyMid != null ? Math.round(monthlyMid * 1.4) : null;
  const photoPath = enr?.photo_path ?? null;
  const photoUrl = engineSingleton.photoUrl(photoPath ?? undefined);
  const city = snapProfile?.location || enr?.location || row.country || enr?.country || null;
  const role = (row.role_type ?? "").toUpperCase().replace(/^ROLE\.FRACTIONAL_?/, "").trim() || null;
  return {
    user_id: row.slug,
    first_name: first ?? null,
    last_name: rest.join(" ") || null,
    avatar_url: photoUrl,
    photo_url: photoUrl,
    intro_video_url: snapProfile?.intro_video_url ?? null,
    linkedin_url: enr?.linkedin_url ?? null,
    headline: row.headline,
    expertise_areas: row.expertise ?? [],
    languages: row.languages ?? [],
    rating_avg: null,
    rating_count: null,
    hourly_eur: monthlyMin,
    hourly_xof: monthlyMin != null ? Math.round(monthlyMin * 655.957) : null,
    price_eur_max: monthlyMax,
    price_xof_max: monthlyMax != null ? Math.round(monthlyMax * 655.957) : null,
    price_unit: "month",
    demo: false,
    city,
    is_new: !row.featured,
    featured: row.featured === true,
    featured_until: row.featured_until ?? null,
    variant: "executive",
    role_label: role ? `${role} ${role ? "Fractional" : ""}`.trim() : null,
    detail_href: `/fractional/${row.slug}`,
  };
}

export function fractionalContentToCard(f: FractionalExecutive, lang: "en" | "fr" = "en"): ProviderCardData {
  const [first, ...rest] = (f.name || "").split(" ");
  return {
    user_id: f.slug,
    first_name: first ?? null,
    last_name: rest.join(" ") || null,
    avatar_url: null,
    headline: f.headline[lang] ?? f.headline.en,
    expertise_areas: f.expertise_tags ?? [],
    languages: f.languages ?? [],
    rating_avg: null,
    rating_count: null,
    hourly_eur: f.monthly_rate_eur_min,
    hourly_xof: f.monthly_rate_xof_min,
    price_eur_max: f.monthly_rate_eur_max,
    price_xof_max: f.monthly_rate_xof_max,
    price_unit: "month",
    demo: f.demo,
    city: f.city,
    is_new: false,
    variant: "executive",
    role_label: `${f.role_type} Fractional`,
    detail_href: `/fractional/${f.slug}`,
  };
}

type MentorRow = {
  id: string;
  slug: string;
  user_id: string | null;
  display_name: string;
  headline_en: string | null;
  headline_fr: string | null;
  expertise: string[] | null;
  markets: string[] | null;
  languages: string[] | null;
  hourly_rate_eur_cents: number | null;
  featured?: boolean | null;
  featured_rank?: number | null;
  featured_until?: string | null;
  rating_avg?: number | null;
  rating_count?: number | null;
  total_sessions_delivered?: number | null;
};

export function mentorRowToCard(row: MentorRow, lang: "en" | "fr"): ProviderCardData {
  const [first, ...rest] = (row.display_name || "").split(" ");
  const hourlyEur = row.hourly_rate_eur_cents != null ? Math.round(Number(row.hourly_rate_eur_cents) / 100) : null;
  const headline = (lang === "fr" ? row.headline_fr : row.headline_en) ?? row.headline_en ?? row.headline_fr ?? null;
  return {
    user_id: row.slug,
    first_name: first ?? null,
    last_name: rest.join(" ") || null,
    avatar_url: null,
    headline,
    expertise_areas: row.expertise ?? [],
    languages: row.languages ?? [],
    rating_avg: row.rating_avg ?? null,
    rating_count: row.rating_count ?? null,
    hourly_eur: hourlyEur,
    hourly_xof: hourlyEur != null ? Math.round(hourlyEur * 655.957) : null,
    price_unit: "hour",
    demo: false,
    city: (row.markets ?? [])[0] ?? null,
    is_new: !row.featured && !(row.rating_count ?? 0),
    featured: row.featured === true,
    featured_until: row.featured_until ?? null,
    variant: "mentor",
    detail_href: `/mentors/${row.slug}`,
  };
}